import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Plus, Pencil, Trash2, Loader2, Search, X, Heart, Share2} from 'lucide-react'
import { getDisplayedLikes, getDisplayedShares, formatCount } from '@/lib/engagement'
import toast from 'react-hot-toast'
import type { NewsArticle } from '@/types'
import InstagramPostMaker from '@/components/InstagramPostMaker'

export default function ListArticles() {
  const [articles, setArticles] = useState<NewsArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [confirmDelete, setConfirmDelete] = useState<NewsArticle | null>(null)

  const fetchArticles = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.news_articles.list({
        sort: { createdAt: -1 },
        limit: 200,
      })
      setArticles(list as NewsArticle[])
    } catch (e) {
      console.error('Erro ao carregar notícias:', e)
      toast.error('Erro ao carregar notícias.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchArticles() }, [fetchArticles])

  const handleDelete = async (article: NewsArticle) => {
    setDeleting(article._id)
    try {
      await lumi.entities.news_articles.delete(article._id)
      toast.success('Notícia excluída com sucesso.')
      setArticles(prev => prev.filter(a => a._id !== article._id))
    } catch (e) {
      console.error('Erro ao excluir notícia:', e)
      toast.error('Erro ao excluir notícia.')
    } finally {
      setDeleting(null)
      setConfirmDelete(null)
    }
  }

  const filtered = articles.filter(a => {
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.authorName.toLowerCase().includes(search.toLowerCase())
    const matchStatus = statusFilter === 'all' || a.status === statusFilter
    return matchSearch && matchStatus
  })

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Listar Notícias</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{articles.length} notícia(s) cadastrada(s)</p>
        </div>
        <Link
          to="/admin/nova-noticia"
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Plus size={16} />
          Nova Notícia
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por título ou autor..."
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
        >
          <option value="all">Todos os status</option>
          <option value="published">Publicados</option>
          <option value="draft">Rascunhos</option>
          <option value="archived">Arquivados</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2">
            <Loader2 size={24} className="animate-spin text-red-600" />
            <span className="text-sm text-gray-500">Carregando...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">
            {search || statusFilter !== 'all' ? 'Nenhuma notícia encontrada com esses filtros.' : 'Nenhuma notícia cadastrada ainda.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {filtered.map(article => (
              <div key={article._id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-16 h-12 rounded-lg object-cover flex-shrink-0 bg-gray-100 dark:bg-gray-800"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{article.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {article.authorName} · {new Date(article.createdAt).toLocaleDateString('pt-BR')}
                    {article.category && ` · ${article.category}`}
                    {' · '}
                    <Heart size={10} className="inline -mt-0.5" /> {formatCount(getDisplayedLikes(article.likes, article.publishedAt || article.createdAt))}
                    {' · '}
                    <Share2 size={10} className="inline -mt-0.5" /> {formatCount(getDisplayedShares(article.shares, article.publishedAt || article.createdAt))}
                  </p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${
                  article.status === 'published'
                    ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400'
                    : article.status === 'draft'
                    ? 'bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                }`}>
                  {article.status === 'published' ? 'Publicado' : article.status === 'draft' ? 'Rascunho' : 'Arquivado'}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <InstagramPostMaker
                    article={article}
                    compact
                    triggerClassName="p-2 rounded-lg text-gray-400 hover:text-[#E1306C] hover:bg-pink-50 dark:hover:bg-pink-950/40 transition-all"
                  />
                  <Link
                    to={`/admin/nova-noticia/${article._id}`}
                    className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 transition-all"
                    aria-label="Editar"
                  >
                    <Pencil size={15} />
                  </Link>
                  <button
                    onClick={() => setConfirmDelete(article)}
                    disabled={deleting === article._id}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-all disabled:opacity-50"
                    aria-label="Excluir"
                  >
                    {deleting === article._id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Excluir Notícia</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Tem certeza que deseja excluir <strong className="text-gray-700 dark:text-gray-300">"{confirmDelete.title}"</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={!!deleting}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 size={15} className="animate-spin" /> : null}
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
